import React, { useCallback, useEffect, useRef, useState } from "react";
import { Mic, Square, Loader2, X, CheckCircle2, AlertTriangle, Radio } from "lucide-react";
import { api, formatApiError } from "../api/client";

/**
 * v46 · Flujo VIP de despacho por voz:
 *   idle → recording → processing → confirming → searching → assigned → (auto-close)
 *
 * Audio pipeline:
 *   - "confirming"  → ElevenLabs mp3 con la interpretación ("Hola, entiendo…")
 *   - "searching"   → loop de waiting_music.wav (bajo volumen)
 *   - "assigned"    → música baja + ElevenLabs con nombre/placa/ETA
 *
 * Fallback: si ElevenLabs falla (red, 502), usamos SpeechSynthesis del navegador.
 */

const BACKEND = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/$/, "");
const TTS_URL = (t) => `${BACKEND}/api/demo/tts?text=${encodeURIComponent(t)}`;
const MUSIC_URL = `${BACKEND}/api/demo/waiting-music`;

function speakFallback(text) {
  try {
    const synth = window.speechSynthesis;
    if (!synth) return;
    synth.cancel();
    const u = new SpeechSynthesisUtterance(text);
    u.lang = "es-ES"; u.pitch = 1.1; u.rate = 0.95;
    synth.speak(u);
  } catch (_) {}
}

// Reproduce un mp3 desde URL con Audio HTMLElement. Resuelve al terminar.
function playAudio(url, { loop = false, volume = 1.0 } = {}) {
  return new Promise((resolve, reject) => {
    const a = new Audio(url);
    a.loop = loop;
    a.volume = volume;
    a.onended = () => resolve(a);
    a.onerror = (e) => reject(e);
    a.play().catch(reject);
    // Devolvemos el elemento inmediatamente si es loop (no espera onended)
    if (loop) resolve(a);
  });
}

export default function VoiceDemoButton() {
  const MAX_MS = 10000;
  const [phase, setPhase] = useState("idle"); // idle|recording|processing|confirming|searching|assigned|error
  const [seconds, setSeconds] = useState(0);
  const [levels, setLevels] = useState([]);
  const [result, setResult] = useState(null);   // interpretación
  const [driver, setDriver] = useState(null);   // info final del conductor
  const [error, setError] = useState("");

  const recorderRef = useRef(null);
  const chunksRef   = useRef([]);
  const streamRef   = useRef(null);
  const timerRef    = useRef(null);
  const meterRef    = useRef(null);
  const audioCtxRef = useRef(null);
  const musicRef    = useRef(null);   // HTMLAudioElement de la música
  const pollRef     = useRef(null);   // intervalo de polling
  const autoCloseRef = useRef(null);

  const cleanup = useCallback(() => {
    try { recorderRef.current?.stream?.getTracks().forEach((t) => t.stop()); } catch (_) {}
    try { streamRef.current?.getTracks().forEach((t) => t.stop()); } catch (_) {}
    try { audioCtxRef.current?.close(); } catch (_) {}
    try { musicRef.current?.pause(); musicRef.current.src = ""; } catch (_) {}
    try { window.speechSynthesis?.cancel(); } catch (_) {}
    if (timerRef.current) clearInterval(timerRef.current);
    if (meterRef.current) cancelAnimationFrame(meterRef.current);
    if (pollRef.current)  clearInterval(pollRef.current);
    if (autoCloseRef.current) clearTimeout(autoCloseRef.current);
    timerRef.current = meterRef.current = pollRef.current = autoCloseRef.current = null;
    audioCtxRef.current = null;
    musicRef.current = null;
    streamRef.current = null;
    recorderRef.current = null;
    chunksRef.current = [];
  }, []);

  useEffect(() => cleanup, [cleanup]);

  const reset = useCallback(() => {
    cleanup();
    setPhase("idle"); setSeconds(0); setLevels([]);
    setResult(null); setDriver(null); setError("");
  }, [cleanup]);

  const start = async () => {
    setError(""); setResult(null); setDriver(null); setSeconds(0); setLevels([]);
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;
      const rec = new MediaRecorder(stream);
      recorderRef.current = rec;
      chunksRef.current = [];
      rec.ondataavailable = (e) => { if (e.data.size > 0) chunksRef.current.push(e.data); };
      rec.onstop = () => { void handleStop(); };
      rec.start();

      const t0 = Date.now();
      timerRef.current = setInterval(() => {
        const s = (Date.now() - t0) / 1000;
        setSeconds(s);
        if (s * 1000 >= MAX_MS && rec.state === "recording") rec.stop();
      }, 100);

      const ctx = new (window.AudioContext || window.webkitAudioContext)();
      audioCtxRef.current = ctx;
      const src = ctx.createMediaStreamSource(stream);
      const analyser = ctx.createAnalyser();
      analyser.fftSize = 256;
      src.connect(analyser);
      const buf = new Uint8Array(analyser.frequencyBinCount);
      const tick = () => {
        analyser.getByteFrequencyData(buf);
        const rms = Math.sqrt(buf.reduce((a, v) => a + v * v, 0) / buf.length) / 255;
        setLevels((prev) => [...prev.slice(-31), Math.max(0.05, Math.min(1, rms * 2.5))]);
        meterRef.current = requestAnimationFrame(tick);
      };
      tick();

      setPhase("recording");
    } catch (e) {
      setPhase("error");
      setError(e?.message || "No pudimos acceder al micrófono. Concede permisos en el navegador.");
    }
  };

  const stopManual = () => {
    if (recorderRef.current?.state === "recording") recorderRef.current.stop();
  };

  // --- Fase Confirmación → Búsqueda → Asignación ------------------------
  const runVipFlow = async (data) => {
    setResult(data);

    // Fase 1: confirmación ElevenLabs
    setPhase("confirming");
    const line1 = data.ok
      ? `Hola, entiendo que vas de ${data.origin || "tu ubicación"} a ${data.destination}. Aguarda un momento mientras te asigno un conductor.`
      : "Registré tu solicitud. Un despachador revisará el destino y se comunicará contigo en breve.";

    try {
      await playAudio(TTS_URL(line1)); // espera hasta que termine (onended)
    } catch (_) {
      speakFallback(line1);
      // esperar aprox 3s a que hable el fallback
      await new Promise((r) => setTimeout(r, 3000));
    }

    if (!data.ride_id) {
      // Sin ride_id (destino no geocodificado): cerramos aquí.
      autoCloseRef.current = setTimeout(reset, 3500);
      return;
    }

    // Fase 2: música de espera en loop + polling del status
    setPhase("searching");
    try {
      const audio = new Audio(MUSIC_URL);
      audio.loop = true;
      audio.volume = 0.55;
      musicRef.current = audio;
      await audio.play();
    } catch (_) { /* si el navegador bloquea autoplay, seguimos sin música */ }

    pollRef.current = setInterval(async () => {
      try {
        const { data: st } = await api.get(`/demo/ride-status/${data.ride_id}`);
        if (st.status === "assigned" || st.status === "accepted") {
          clearInterval(pollRef.current); pollRef.current = null;
          await handleAssigned(st);
        }
      } catch (_) { /* soft-fail, siguiente tick lo reintenta */ }
    }, 2000);

    // Timeout defensivo: si nadie asigna en 60s, cerramos con mensaje.
    setTimeout(() => {
      if (pollRef.current) {
        clearInterval(pollRef.current); pollRef.current = null;
        stopMusicSoft();
        setDriver({ status: "timeout" });
        setPhase("assigned");
        const line = "Aún estamos coordinando un conductor. Recibirás una llamada en unos minutos.";
        playAudio(TTS_URL(line)).catch(() => speakFallback(line));
        autoCloseRef.current = setTimeout(reset, 6000);
      }
    }, 60000);
  };

  const stopMusicSoft = async () => {
    const a = musicRef.current;
    if (!a) return;
    // Fade-out 800ms
    const steps = 12;
    const step = a.volume / steps;
    for (let i = 0; i < steps; i++) {
      a.volume = Math.max(0, a.volume - step);
      await new Promise((r) => setTimeout(r, 60));
    }
    try { a.pause(); } catch (_) {}
    musicRef.current = null;
  };

  const handleAssigned = async (st) => {
    setDriver(st);
    setPhase("assigned");
    await stopMusicSoft();
    const line = `¡Listo! El conductor ${st.driver_name || "asignado"} va en camino en un ${st.vehicle_model || "vehículo"} placa ${st.vehicle_plate || "en confirmación"}. Llegará en ${st.eta_minutes || 5} minutos.`;
    try { await playAudio(TTS_URL(line)); }
    catch (_) {
      speakFallback(line);
      await new Promise((r) => setTimeout(r, 4500));
    }
    autoCloseRef.current = setTimeout(reset, 3000);
  };

  const handleStop = async () => {
    setPhase("processing");
    if (timerRef.current) { clearInterval(timerRef.current); timerRef.current = null; }
    if (meterRef.current) { cancelAnimationFrame(meterRef.current); meterRef.current = null; }
    try {
      const blob = new Blob(chunksRef.current, { type: recorderRef.current?.mimeType || "audio/webm" });
      const fd = new FormData();
      fd.append("audio", blob, `demo-${Date.now()}.webm`);
      const { data } = await api.post("/demo/process-voice", fd, {
        headers: { "Content-Type": "multipart/form-data" },
        timeout: 45000,
      });
      // Cerramos stream/mic ANTES de arrancar el flujo VIP.
      try { streamRef.current?.getTracks().forEach((t) => t.stop()); } catch (_) {}
      try { audioCtxRef.current?.close(); } catch (_) {}
      streamRef.current = null; audioCtxRef.current = null; recorderRef.current = null;

      await runVipFlow(data);
    } catch (e) {
      setError(formatApiError(e) || "No pudimos procesar el audio.");
      setPhase("error");
    }
  };

  // --- UI --------------------------------------------------------------
  return (
    <>
      <button
        onClick={start}
        disabled={phase !== "idle"}
        data-testid="voice-demo-fab"
        className="fixed bottom-6 right-6 z-40 flex items-center gap-2 px-5 py-3 rounded-full
                   bg-[#C6E90C] text-black font-bold shadow-lg hover:shadow-xl hover:brightness-110
                   transition disabled:opacity-40 disabled:cursor-not-allowed"
      >
        <Mic className="w-5 h-5" /> 🎤 Probar IA de Voz
      </button>

      {phase !== "idle" && (
        <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4"
             onClick={phase === "error" ? reset : undefined}>
          <div className="bg-white rounded-2xl max-w-md w-full p-6" onClick={(e) => e.stopPropagation()}
               data-testid="voice-demo-modal">
            {phase === "recording" && (
              <>
                <div className="flex items-center justify-between mb-4">
                  <h3 className="font-bold text-lg flex items-center gap-2">
                    <span className="w-2.5 h-2.5 bg-red-500 rounded-full animate-pulse" /> Grabando…
                  </h3>
                  <div className="text-sm font-mono text-neutral-500">{seconds.toFixed(1)}s / 10s</div>
                </div>
                <div className="h-20 flex items-end justify-center gap-1 bg-neutral-50 rounded-lg p-3 mb-4">
                  {(levels.length ? levels : Array(20).fill(0.05)).map((v, i) => (
                    <span key={i} style={{ height: `${Math.round(v * 100)}%` }}
                          className="w-1.5 bg-emerald-500 rounded transition-all" />
                  ))}
                </div>
                <button onClick={stopManual}
                        className="w-full bg-red-600 text-white font-bold py-3 rounded-lg flex items-center justify-center gap-2"
                        data-testid="voice-demo-stop">
                  <Square className="w-4 h-4" /> Detener y procesar
                </button>
                <p className="text-xs text-neutral-500 mt-3 text-center">
                  Ejemplo: <i>“Recoger desde la Plaza al Aeropuerto”</i>.
                </p>
              </>
            )}

            {phase === "processing" && (
              <div className="text-center py-6">
                <Loader2 className="w-10 h-10 animate-spin text-emerald-600 mx-auto mb-3" />
                <div className="font-bold">Procesando con Whisper + IA…</div>
                <div className="text-xs text-neutral-500 mt-1">Transcripción, extracción y agendado.</div>
              </div>
            )}

            {phase === "confirming" && result && (
              <div className="text-center py-4">
                <div className="w-14 h-14 bg-emerald-100 text-emerald-700 rounded-full flex items-center justify-center mx-auto mb-3 animate-pulse">
                  <Radio className="w-7 h-7" />
                </div>
                <div className="font-bold text-lg">Operadora respondiendo…</div>
                <div className="text-sm text-neutral-600 mt-2">
                  "Entiendo que vas de <b>{result.origin || "tu ubicación"}</b> a <b>{result.destination}</b>."
                </div>
              </div>
            )}

            {phase === "searching" && (
              <div className="text-center py-4">
                <div className="w-14 h-14 bg-amber-100 text-amber-700 rounded-full flex items-center justify-center mx-auto mb-3">
                  <Loader2 className="w-7 h-7 animate-spin" />
                </div>
                <div className="font-bold text-lg">Buscando conductor cercano…</div>
                <div className="text-xs text-neutral-500 mt-1">🎵 Aguarda un momento en línea.</div>
                <div className="mt-4 bg-neutral-50 rounded-lg p-3 text-xs space-y-1 text-left">
                  <div><span className="text-neutral-500 font-mono">DE ›</span> {result?.origin || "—"}</div>
                  <div><span className="text-neutral-500 font-mono">A ›</span> {result?.destination || "—"}</div>
                </div>
              </div>
            )}

            {phase === "assigned" && (
              <>
                <div className="flex items-start gap-3 mb-4">
                  <CheckCircle2 className="w-8 h-8 text-emerald-600 flex-shrink-0" />
                  <div>
                    <div className="font-bold text-lg">¡Conductor en camino!</div>
                    <div className="text-sm text-neutral-600 mt-0.5">
                      {driver?.driver_name || "Conductor asignado"} · llega en {driver?.eta_minutes || 5} min.
                    </div>
                  </div>
                </div>
                <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-4 space-y-1 text-sm">
                  <div><b>Nombre:</b> {driver?.driver_name || "—"}</div>
                  <div><b>Vehículo:</b> {driver?.vehicle_model || "—"}</div>
                  <div><b>Placa:</b> <span className="font-mono font-bold">{driver?.vehicle_plate || "—"}</span></div>
                  <div><b>ETA:</b> {driver?.eta_minutes || 5} min</div>
                </div>
                <button onClick={reset}
                        className="w-full mt-4 bg-black text-white font-bold py-2.5 rounded-lg"
                        data-testid="voice-demo-close">
                  Cerrar
                </button>
              </>
            )}

            {phase === "error" && (
              <>
                <div className="flex items-start gap-3 mb-4">
                  <AlertTriangle className="w-7 h-7 text-red-600 flex-shrink-0" />
                  <div>
                    <div className="font-bold text-lg">Algo falló</div>
                    <div className="text-sm text-neutral-600 mt-0.5">{error}</div>
                  </div>
                </div>
                <button onClick={reset}
                        className="w-full bg-black text-white font-bold py-3 rounded-lg"
                        data-testid="voice-demo-close">
                  Cerrar
                </button>
              </>
            )}
          </div>
        </div>
      )}
    </>
  );
}
